import { h, vaciar, rebotar, num } from './dom';
import { Almacen, ESTADO_INICIAL, type Estado, type CapaBase } from './estado';
import type { Contexto } from './contexto';
import { ClienteDb } from '../db/cliente';
import { Mapa, type Vista } from '../map/mapa';
import { leerRuta, rutaAUrl, esEscena, MAPA_VACIO, ESCENA_VACIA, type Ruta, type PantallaEscena, type EscenaUrl } from './rutas';
import { compartirEnlace } from './compartir';
import { ZonaPanel } from './paneles/ZonaPanel';
import { BuscarPanel } from './paneles/BuscarPanel';
import { CandidatosPanel } from './paneles/CandidatosPanel';
import { FichaPanel } from './paneles/FichaPanel';
import { abrirAyuda } from './paneles/Ayuda';
import { abrirComparador } from './paneles/Comparador';
import { abrirAvanzado } from './paneles/Avanzado';
import { CAPAS_BASE, ANOS_HISTORICO } from '../map/capas';
import { esReferenciaCatastral, consultarRcPorCoordenadas } from '../catastro/ovc';
import { esCoordenada, buscarLugar } from '../catastro/cartociudad';
import { descargarGml, urlParcelaPorRc } from '../catastro/wfs';
import { parsearParcelas } from '../catastro/gml';
import { aUtm, aLonLat, formatearUtm, husoParaLonLat } from '../geo/proyecciones';
import { bboxMultiPoligono } from '../geo/geometria';
import { prepararLidarPunto, asegurarEntornoParcela, RADIO_LIDAR_M } from '../lidar/preparar';
import { claveModelo, type EstadoArea } from '../lidar/api';
import type { EscenaModelo } from '../lidar/escenaModelo';
import type { Ficha } from '../db/consultas';
import { botonMenu, consultaMovil, ponerMenu } from './movil';

const VISTA_INICIAL: Vista = { centro: [-3.7038, 40.4168], zoom: 6, bearing: 0, pitch: 0 };
const ZOOM_VECTOR = 13;

/**
 * Composicion de la aplicacion: cabecera, panel lateral con pestanas, mapa, ficha y pie. En movil y
 * tableta el mapa ocupa toda la pantalla y la cabecera, el panel y el pie van en un cajon lateral que
 * abre el boton ☰.
 */
export class App {
  readonly almacen = new Almacen();
  readonly db = new ClienteDb();
  mapa!: Mapa;
  private ctx!: Contexto;
  private ficha!: FichaPanel;
  private zona!: ZonaPanel;
  private buscar!: BuscarPanel;
  private candidatos!: CandidatosPanel;
  private coordsEl = h('span', { class: 'coords' });
  private medicionEl = h('span', { class: 'medicion' });
  private avisoEl = h('div', { class: 'aviso oculto' });
  private lateralEl = h('aside', { class: 'panel-lateral' });
  private cuerpoLateral = h('div', { class: 'panel-cuerpo' });
  private cabeceraEl!: HTMLElement;
  private pieEl!: HTMLElement;
  private cuerpoEl!: HTMLElement;
  private readonly cajonEl = h('div', { class: 'menu-cajon', role: 'dialog', 'aria-label': 'Menú' });
  private botonMenuEl!: HTMLButtonElement;
  /** Escena del modelo abierta, con su parcela. */
  private escena: EscenaModelo | null = null;
  private escenaRc: string | null = null;
  /** La escena tiene su entrada en el historial: Atras (o «Volver al mapa») vuelve a la parcela. */
  private escenaEnHistorial = false;
  /** La proxima escena se abre en la entrada actual del historial (Adelante hacia una escena). */
  private reemplazarAlAbrir = false;
  /** Estado de la escena que pide un enlace, hasta que se abre. */
  private inicioEscena: { rc: string; pantalla: PantallaEscena; escena: EscenaUrl; entorno: Promise<Ficha | null> } | null = null;
  private readonly escribirUrlRebotado = rebotar(() => this.escribirUrl(), 300);

  /**
   * El informe PDF (pdf-lib, unos 500 KB con sus fuentes) y el panel de DXF se cargan al pedirlos: no van
   * en el arranque de la pagina.
   */
  onInformePdf: ((rcs: string[]) => void) | null = (rcs) => void this.bajoDemanda('el informe PDF', () => import('./informe'))
    .then((m) => m?.generarInformeCandidatos(this.ctx, rcs));
  private abrirDxf(rc: string | null): void {
    void this.bajoDemanda('el DXF', () => import('./paneles/ExportarDxf')).then((m) => m?.abrirExportarDxf(this.ctx, rc));
  }

  /** Modulo cargado al usarlo por primera vez; si no llega (red), un aviso y null. */
  private async bajoDemanda<T>(que: string, cargar: () => Promise<T>): Promise<T | null> {
    try {
      return await cargar();
    } catch (err) {
      this.almacen.avisar(`No se pudo cargar ${que}: ${err instanceof Error ? err.message : String(err)}`, 'error');
      return null;
    }
  }

  constructor(private readonly raiz: HTMLElement) {}

  async montar(): Promise<void> {
    const ruta = leerRuta(location.pathname, location.search, location.hash);
    const vista = ruta.mapa.vista ?? VISTA_INICIAL;
    vaciar(this.raiz);

    const mapaEl = h('div', { class: 'mapa', id: 'mapa' });
    this.cabeceraEl = this.cabecera();
    this.pieEl = this.pie();
    this.cuerpoEl = h('div', { class: 'cuerpo' }, this.lateralEl, mapaEl);
    this.botonMenuEl = botonMenu(this.raiz);
    this.cajonEl.append(h('div', { class: 'menu-cajon-cierre' },
      h('button', { class: 'cerrar', 'aria-label': 'Cerrar el menú', onClick: () => this.cerrarMenu() }, '×')));
    this.raiz.append(
      this.cabeceraEl,
      this.cuerpoEl,
      this.pieEl,
      this.avisoEl,
      h('div', { class: 'menu-fondo', onClick: () => this.cerrarMenu() }),
      this.cajonEl,
      this.botonMenuEl,
    );
    this.colocarSegunPantalla();
    consultaMovil()?.addEventListener('change', () => this.colocarSegunPantalla());
    this.mapa = new Mapa(mapaEl, vista);

    this.ctx = {
      almacen: this.almacen, db: this.db, mapa: this.mapa,
      seleccionar: (rc, centrar) => this.seleccionar(rc, centrar),
      refrescarZona: () => this.refrescarZona(),
      refrescarCandidatos: () => this.refrescarCandidatos(),
      refrescarResumen: () => this.refrescarResumen(),
      alternarCandidato: (rc) => this.alternarCandidato(rc),
    };
    this.zona = new ZonaPanel(this.ctx);
    this.buscar = new BuscarPanel(this.ctx);
    this.candidatos = new CandidatosPanel(this.ctx);
    this.ficha = new FichaPanel(this.ctx);
    this.ficha.onCerrar = () => void this.seleccionar(null);
    this.ficha.onExportarDxf = (rc) => this.abrirDxf(rc);
    this.ficha.onInformePdf = (rc) => this.onInformePdf?.([rc]);
    this.ficha.onCompartir = (rc) => void this.compartir(`Parcela ${rc} · Cernicalo`).then((t) => this.almacen.avisar(t));
    this.ficha.onVer3d = (rc, area) => this.abrirEscena(rc, area);
    this.candidatos.onComparar = () => abrirComparador(this.almacen.estado.candidatos,
      () => this.onInformePdf?.(this.almacen.estado.candidatos.map((c) => c.rc)), (rc) => void this.seleccionar(rc, true));
    this.candidatos.onInformePdf = () => this.onInformePdf?.(this.almacen.estado.candidatos.map((c) => c.rc));
    this.zona.onAvanzado = () => abrirAvanzado(this.db, this.almacen.estado.resumen?.epsg ?? null);
    this.zona.onExportarDxf = () => this.abrirDxf(null);
    this.raiz.append(this.ficha.el);
    this.lateralEl.append(this.pestanas(), this.cuerpoLateral);

    if (ruta.mapa.base) this.almacen.set({ base: ruta.mapa.base });
    if (ruta.mapa.catastro !== null) this.almacen.set({ catastroWms: ruta.mapa.catastro > 0, catastroOpacidad: ruta.mapa.catastro || ESTADO_INICIAL.catastroOpacidad });
    if (ruta.mapa.historico) this.almacen.set({ ortoHistorica: ruta.mapa.historico });
    this.almacen.suscribir((e, cambios) => this.reaccionar(e, cambios));
    // La direccion pasa al formato nuevo (los enlaces de antes lo traian todo en el hash) y una escena se
    // abre encima de la entrada de su parcela, para que Atras vuelva a ella.
    history.replaceState(null, '', rutaAUrl(esEscena(ruta.pantalla) ? { ...ruta, pantalla: 'parcela', escena: { ...ESCENA_VACIA } } : ruta));
    window.addEventListener('popstate', () => void this.alNavegar());

    // Al descargar la pagina se liberan los ficheros de OPFS para que la siguiente carga los encuentre libres.
    window.addEventListener('pagehide', () => this.db.pausar());
    // La base de datos arranca en paralelo con el mapa: no depende de que lleguen las teselas.
    const arranqueDb = this.db.iniciar().then((inicio) => {
      this.almacen.set({ persistente: inicio.persistente });
      if (!inicio.persistente) this.almacen.avisar('Tu navegador no permite guardar la base de datos (OPFS): los datos se perderán al cerrar la pestaña.', 'error');
    }).catch((err: unknown) => {
      this.almacen.set({ persistente: false });
      this.almacen.avisar(`No se pudo abrir la base de datos local: ${err instanceof Error ? err.message : String(err)}`, 'error');
    });
    this.renderLateral(this.almacen.estado);

    await this.mapa.listo;
    this.aplicarCapas(this.almacen.estado);
    this.conectarMapa();

    await arranqueDb;
    await this.refrescarResumen();
    await this.refrescarCandidatos();
    await this.refrescarZona();
    if (ruta.mapa.modo3d) this.almacen.set({ modo3d: true });
    if (ruta.rc) await this.abrirRuta(ruta, !ruta.mapa.vista);
  }

  // ---------- Enlaces ----------

  /**
   * Parcela de un enlace y, si lo pide, su modelo 3D, el paseo o el dron. La parcela se baja del
   * Catastro con su entorno (quien abre el enlace no la tiene en su base local); el modelo se abre
   * con el area del enlace si sigue en el servidor y, si no, se calcula de nuevo.
   */
  private async abrirRuta(r: Ruta, centrar: boolean): Promise<void> {
    if (!r.rc) return;
    const epsg = await this.mostrarParcela(r.rc, centrar);
    const entorno = this.cargarEntorno(r.rc, epsg);
    if (!esEscena(r.pantalla)) { await entorno; return; }
    this.inicioEscena = { rc: r.rc, pantalla: r.pantalla, escena: r.escena, entorno };
    await this.ficha.verEn3d(r.rc, false, { areaId: r.escena.area, margenM: r.escena.desfase, entorno });
  }

  /** Selecciona la parcela y abre su ficha (encuadrada si hace falta); devuelve el huso en que pedirla. */
  private async mostrarParcela(rc: string, centrar: boolean): Promise<number> {
    this.almacen.set({ rcSeleccionado: rc });
    this.mapa.setSeleccion(rc);
    const f = await this.db.ficha(rc);
    if (f) {
      this.ficha.mostrar(f);
      if (centrar) this.mapa.encajarBbox([f.parcela.minx, f.parcela.miny, f.parcela.maxx, f.parcela.maxy], f.parcela.epsg, 18);
      return f.parcela.epsg;
    }
    this.ficha.mostrar(null, { rc, direccion: null });
    const c = this.mapa.map.getCenter();
    const [lon, lat] = (await this.localizarReferencia(rc, centrar)) ?? [c.lng, c.lat];
    const epsg = husoParaLonLat(lon, lat);
    if (this.ficha.rcActual === rc) this.ficha.mostrar(null, { rc, direccion: null, epsg });
    return epsg;
  }

  /**
   * Catastro de la parcela de un enlace y de su entorno (lo que baja «Ver 3D»): la ficha sale completa
   * y resaltada en el mapa, y el modo de juego tiene las plantas de los edificios. Si ya estaba
   * descargado, no vuelve a la red.
   */
  private async cargarEntorno(rc: string, epsg: number): Promise<Ficha | null> {
    try {
      const f = await asegurarEntornoParcela(this.ctx, rc, epsg, (m) => this.almacen.avisar(m));
      await this.refrescarResumen();
      await this.refrescarCandidatos();
      await this.refrescarZona();
      if (this.almacen.estado.rcSeleccionado === rc && this.ficha.rcActual === rc) {
        this.mapa.setSeleccion(rc);
        this.ficha.mostrar((await this.db.ficha(rc)) ?? f);
      }
      return f;
    } catch (err) {
      this.almacen.avisar(`No se pudo bajar la parcela del Catastro: ${err instanceof Error ? err.message : String(err)}`, 'error');
      return null;
    }
  }

  /** Escena del modelo de una parcela, con su entrada en el historial y su direccion. */
  private async abrirEscena(rc: string, area: EstadoArea): Promise<void> {
    const inicio = this.inicioEscena?.rc === rc ? this.inicioEscena : null;
    this.inicioEscena = null;
    this.escena?.cerrar();
    const { abrirEscenaModelo } = await import('../lidar/escenaModelo');
    await abrirEscenaModelo(this.ctx, area, {
      titulo: rc, clave: claveModelo({ rc }), nombreFichero: `cernicalo-parcela-${rc}`,
      inicio: inicio ? { ...inicio.escena, juego: inicio.pantalla === '3d' ? null : inicio.pantalla } : undefined,
      antesDelJuego: inicio?.entorno,
      alAbrir: (escena) => {
        this.escena = escena;
        this.escenaRc = rc;
        if (this.reemplazarAlAbrir) history.replaceState(null, '', this.urlActual());
        else history.pushState(null, '', this.urlActual());
        this.reemplazarAlAbrir = false;
        this.escenaEnHistorial = true;
      },
      alCambiar: () => this.escribirUrlRebotado(),
      alCerrar: () => {
        this.escena = null;
        this.escenaRc = null;
        if (this.escenaEnHistorial) {
          this.escenaEnHistorial = false;
          history.back();
        } else {
          this.escribirUrl();
        }
      },
      alCompartir: () => this.compartir(`Parcela ${rc} en 3D · Cernicalo`),
    });
  }

  /** Atras y Adelante del navegador: cierran la escena del modelo o la vuelven a abrir. */
  private async alNavegar(): Promise<void> {
    const r = leerRuta(location.pathname, location.search, location.hash);
    if (this.escena) {
      if (!esEscena(r.pantalla)) {
        // La entrada de la escena ya no esta: se cierra sin volver atras otra vez.
        this.escenaEnHistorial = false;
        this.escena.cerrar();
      }
      return;
    }
    if (esEscena(r.pantalla) && r.rc) {
      this.reemplazarAlAbrir = true;
      try { await this.abrirRuta(r, false); } finally { this.reemplazarAlAbrir = false; }
      return;
    }
    if (r.rc !== this.almacen.estado.rcSeleccionado) await this.seleccionar(r.rc, false);
  }

  /** Direccion de lo que se ve: la escena abierta o el mapa con la parcela seleccionada. */
  private urlActual(): string {
    if (this.escena && this.escenaRc) {
      const s = this.escena.estadoUrl();
      return rutaAUrl({ pantalla: s.pantalla, rc: this.escenaRc, mapa: { ...MAPA_VACIO }, escena: s.escena });
    }
    const e = this.almacen.estado;
    // Solo lo que no es lo de siempre, para que los enlaces sean cortos.
    const catastro = !e.catastroWms ? 0 : e.catastroOpacidad !== ESTADO_INICIAL.catastroOpacidad ? e.catastroOpacidad : null;
    return rutaAUrl({
      pantalla: e.rcSeleccionado ? 'parcela' : 'mapa',
      rc: e.rcSeleccionado,
      mapa: { vista: this.mapa.vista(), base: e.base !== ESTADO_INICIAL.base ? e.base : null, catastro, modo3d: e.modo3d, historico: e.ortoHistorica },
      escena: { ...ESCENA_VACIA },
    });
  }

  /** Comparte (o copia) la direccion de lo que se ve; devuelve el texto para el usuario. */
  private async compartir(titulo: string): Promise<string> {
    this.escribirUrl();
    return compartirEnlace(location.origin + this.urlActual(), titulo);
  }

  // ---------- Layout ----------

  /** En movil y tableta, cabecera, panel y pie dentro del cajon; si no, en su sitio de siempre. */
  private colocarSegunPantalla(): void {
    if (consultaMovil()?.matches) {
      this.cajonEl.append(this.cabeceraEl, this.lateralEl, this.pieEl);
      // La medicion flota sobre el mapa: el pie esta en el cajon, que se cierra al medir.
      this.raiz.append(this.medicionEl);
    } else {
      this.cerrarMenu();
      this.raiz.insertBefore(this.cabeceraEl, this.cuerpoEl);
      this.cuerpoEl.prepend(this.lateralEl);
      this.cuerpoEl.after(this.pieEl);
      this.coordsEl.after(this.medicionEl);
    }
  }

  private cerrarMenu(): void {
    ponerMenu(this.raiz, false, this.botonMenuEl);
  }

  private cabecera(): HTMLElement {
    const entrada = h('input', {
      type: 'search', class: 'buscador', placeholder: 'Dirección, municipio, referencia catastral o coordenadas…',
      onKeydown: (ev: KeyboardEvent) => { if (ev.key === 'Enter') void this.buscarLugar((ev.target as HTMLInputElement).value); },
    });
    return h('header', { class: 'cabecera' },
      h('div', { class: 'logo' }, h('span', { class: 'logo-marca' }, '▲'), ' Cernicalo ', h('span', { class: 'logo-sufijo' }, 'Online')),
      h('div', { class: 'buscador-caja' }, entrada, h('button', { class: 'boton', onClick: () => void this.buscarLugar(entrada.value) }, 'Ir')),
      h('div', { class: 'cabecera-botones' },
        h('button', { class: 'boton secundario', id: 'boton-medir', title: 'Medir distancia y área', onClick: () => this.alternarHerramienta('medir') }, '📏'),
        h('button', { class: 'boton secundario', id: 'boton-3d', onClick: () => this.almacen.set({ modo3d: !this.almacen.estado.modo3d }) }, '3D'),
        h('button', { class: 'boton secundario', onClick: (ev: MouseEvent) => this.abrirCapas(ev.currentTarget as HTMLElement) }, 'Capas'),
        h('button', { class: 'boton secundario', onClick: () => abrirAyuda() }, 'Ayuda')));
  }

  private pestanas(): HTMLElement {
    const mk = (id: Estado['panelLateral'], texto: string) => h('button', {
      class: 'pestana', dataset: { panel: id }, onClick: () => this.almacen.set({ panelLateral: id, panelAbierto: true }),
    }, texto);
    return h('nav', { class: 'pestanas' }, mk('zona', 'Zona'), mk('buscar', 'Buscar'), mk('candidatos', 'Candidatos'));
  }

  private renderLateral(e: Estado): void {
    vaciar(this.cuerpoLateral);
    const panel = e.panelLateral === 'zona' ? this.zona.el : e.panelLateral === 'buscar' ? this.buscar.el : this.candidatos.el;
    this.cuerpoLateral.append(panel);
    for (const b of this.lateralEl.querySelectorAll<HTMLElement>('.pestana')) b.classList.toggle('activa', b.dataset.panel === e.panelLateral);
    this.lateralEl.classList.toggle('abierto', e.panelAbierto);
  }

  private pie(): HTMLElement {
    return h('footer', { class: 'pie' }, this.coordsEl, this.medicionEl,
      h('span', { class: 'pie-derecha' }, h('a', { href: 'https://www.ign.es', target: '_blank', rel: 'noopener' }, '© IGN'), ' · ',
        h('a', { href: 'https://www.sedecatastro.gob.es', target: '_blank', rel: 'noopener' }, '© Catastro')));
  }

  private abrirCapas(ancla: HTMLElement): void {
    document.querySelector('.popover')?.remove();
    // En el movil las capas salen como hoja desde abajo (CSS), con el cajon cerrado.
    if (consultaMovil()?.matches) this.cerrarMenu();
    const e = this.almacen.estado;
    const pop = h('div', { class: 'popover' },
      h('h3', null, 'Mapa base'),
      ...(Object.keys(CAPAS_BASE) as CapaBase[]).map((k) => h('label', { class: 'check' },
        h('input', { type: 'radio', name: 'base', checked: e.base === k, onChange: () => this.almacen.set({ base: k }) }), ' ', CAPAS_BASE[k].nombre)),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: e.rotulos, onChange: (ev: Event) => this.almacen.set({ rotulos: (ev.target as HTMLInputElement).checked }) }), ' Rótulos sobre la ortofoto'),
      h('h3', null, 'Catastro'),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: e.catastroWms, onChange: (ev: Event) => this.almacen.set({ catastroWms: (ev.target as HTMLInputElement).checked }) }), ' Cartografía catastral (WMS)'),
      h('label', { class: 'campo' }, 'Opacidad ', h('input', { type: 'range', min: '0', max: '1', step: '0.05', value: String(e.catastroOpacidad), onInput: (ev: Event) => this.almacen.set({ catastroOpacidad: Number((ev.target as HTMLInputElement).value) }) })),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: e.zonaVector, onChange: (ev: Event) => this.almacen.set({ zonaVector: (ev.target as HTMLInputElement).checked }) }), ' Parcelas cargadas (vectorial)'),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: e.edificios3d, onChange: (ev: Event) => this.almacen.set({ edificios3d: (ev.target as HTMLInputElement).checked }) }), ' Edificios extruidos en 3D'),
      h('h3', null, 'IGN'),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: e.aguaVisible, onChange: (ev: Event) => this.almacen.set({ aguaVisible: (ev.target as HTMLInputElement).checked }) }), ' Ríos y arroyos (cargados con la zona)'),
      h('h3', null, 'Ortofoto histórica'),
      h('select', { onChange: (ev: Event) => { const v = (ev.target as HTMLSelectElement).value; this.almacen.set({ ortoHistorica: v || null }); } },
        h('option', { value: '', selected: !e.ortoHistorica }, 'Ninguna'),
        ...ANOS_HISTORICO.map((a) => h('option', { value: a, selected: e.ortoHistorica === a }, a.replace('PNOA', 'PNOA ')))),
      h('h3', null, 'Terreno 3D'),
      h('label', { class: 'campo' }, 'Exageración ', h('input', { type: 'range', min: '1', max: '3', step: '0.25', value: String(e.exageracion), onInput: (ev: Event) => this.almacen.set({ exageracion: Number((ev.target as HTMLInputElement).value) }) })),
      h('button', {
        class: 'boton secundario', disabled: !e.modo3d || this.nubeOcupada,
        title: `Descarga las hojas LiDAR del IGN alrededor del centro de la vista (±${RADIO_LIDAR_M} m) y las abre enteras en 3D`,
        onClick: () => { pop.remove(); void this.nubeCompleta(); },
      }, this.nubeOcupada ? 'Preparando la nube…' : 'Nube LiDAR completa (hojas IGN)'),
      e.modo3d ? null : h('div', { class: 'silencio' }, 'Activa el 3D para ver la nube completa.'),
    );
    const r = ancla.getBoundingClientRect();
    pop.style.top = `${r.bottom + 6}px`;
    pop.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
    document.body.append(pop);
    const cerrar = (ev: MouseEvent) => { if (!pop.contains(ev.target as Node) && ev.target !== ancla) { pop.remove(); document.removeEventListener('mousedown', cerrar); } };
    setTimeout(() => document.addEventListener('mousedown', cerrar), 0);
  }

  private nubeOcupada = false;

  /** Modo 3D: nube LiDAR completa (hojas del IGN sin recortar) alrededor del centro de la vista. */
  private async nubeCompleta(): Promise<void> {
    if (this.nubeOcupada) return;
    this.nubeOcupada = true;
    const c = this.mapa.map.getCenter();
    const epsg = this.almacen.estado.resumen?.epsg ?? this.mapa.epsgVista();
    const u = aUtm(c.lng, c.lat, epsg);
    let ultimo = '';
    try {
      const area = await prepararLidarPunto(this.ctx, u.x, u.y, epsg, {
        onEstado: (a) => {
          const t = `LiDAR: ${a.mensaje ?? a.estado}${a.hojasTotal ? ` (${a.hojasHechas}/${a.hojasTotal} hojas)` : ''}`;
          if (t !== ultimo && a.estado !== 'listo') { ultimo = t; this.almacen.avisar(t); }
        },
      });
      const { abrirEscenaLidar } = await import('../lidar/escena');
      await abrirEscenaLidar(this.ctx, area, null);
    } catch (err) {
      this.almacen.avisar(`No se pudo abrir la nube LiDAR: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      this.nubeOcupada = false;
    }
  }

  // ---------- Reacciones al estado ----------

  private reaccionar(e: Estado, cambios: Partial<Estado>): void {
    // En movil, al elegir una parcela (se abre su ficha) o una herramienta del mapa, el cajon se quita de en medio.
    if (('rcSeleccionado' in cambios && e.rcSeleccionado) || ('herramienta' in cambios && e.herramienta !== 'ninguna') || ('modo3d' in cambios)) this.cerrarMenu();
    // Y vuelve cuando un paso pide el panel (p. ej. al terminar de dibujar la zona, para seguir en ella).
    else if ('panelAbierto' in cambios && e.panelAbierto && consultaMovil()?.matches) ponerMenu(this.raiz, true, this.botonMenuEl);
    if ('base' in cambios || 'rotulos' in cambios || 'catastroWms' in cambios || 'catastroOpacidad' in cambios || 'zonaVector' in cambios || 'ortoHistorica' in cambios || 'edificios3d' in cambios || 'aguaVisible' in cambios) this.aplicarCapas(e);
    if ('modo3d' in cambios) {
      this.mapa.set3d(e.modo3d, e.exageracion);
      document.getElementById('boton-3d')?.classList.toggle('activo', e.modo3d);
      if (e.modo3d) void this.refrescarPartes();
      this.escribirUrl();
    }
    if ('exageracion' in cambios) this.mapa.setExageracion(e.exageracion);
    if ('panelLateral' in cambios || 'panelAbierto' in cambios) this.renderLateral(e);
    if ('aviso' in cambios && e.aviso) this.mostrarAviso(e.aviso.texto, e.aviso.tipo);
    if ('herramienta' in cambios) document.getElementById('boton-medir')?.classList.toggle('activo', e.herramienta === 'medir');
    if ('base' in cambios || 'catastroOpacidad' in cambios || 'catastroWms' in cambios || 'rcSeleccionado' in cambios || 'ortoHistorica' in cambios) this.escribirUrl();
  }

  private aplicarCapas(e: Estado): void {
    this.mapa.setBase(e.base);
    this.mapa.setRotulos(e.rotulos && e.base === 'orto');
    this.mapa.setCatastroWms(e.catastroWms, e.catastroOpacidad);
    this.mapa.setZonaVisible(e.zonaVector);
    this.mapa.setOrtoHistorica(e.ortoHistorica);
    this.mapa.setEdificios3d(e.edificios3d);
    this.mapa.setAguaVisible(e.aguaVisible);
  }

  private conectarMapa(): void {
    this.mapa.onCursor = (p) => {
      if (!p) { this.coordsEl.textContent = ''; return; }
      const u = aUtm(p[0], p[1]);
      this.coordsEl.textContent = `${p[1].toFixed(5)}, ${p[0].toFixed(5)} · ${formatearUtm(u.x, u.y, u.epsg)}`;
    };
    this.mapa.onMovimiento = rebotar(() => { this.escribirUrl(); void this.refrescarZona(); if (this.almacen.estado.modo3d) void this.refrescarPartes(); }, 250);
    this.mapa.onClickParcela = ({ rc, lonLat }) => void this.clic(rc, lonLat);
    this.mapa.onZonaDibujada = ({ bbox, epsg }) => { this.almacen.set({ zona: { bbox, epsg }, herramienta: 'ninguna', panelLateral: 'zona', panelAbierto: true }); this.mapa.mostrarZona({ bbox, epsg }); };
    this.mapa.onMedicion = (m) => {
      this.medicionEl.textContent = m ? `Medición: ${num(m.longitudM, 1)} m${m.areaM2 !== null ? ` · ${num(m.areaM2)} m²` : ''} (doble clic para terminar)` : '';
    };
  }

  private alternarHerramienta(hta: 'medir'): void {
    const activa = this.almacen.estado.herramienta === hta;
    this.almacen.set({ herramienta: activa ? 'ninguna' : hta });
    this.mapa.setHerramienta(activa ? 'ninguna' : hta);
  }

  /** La direccion de la pagina sigue a lo que se ve (sin recargar ni anadir entradas al historial). */
  private escribirUrl(): void {
    const url = this.urlActual();
    if (url !== location.pathname + location.search || location.hash) history.replaceState(null, '', url);
  }

  private mostrarAviso(texto: string, tipo: 'info' | 'error'): void {
    this.avisoEl.textContent = texto;
    this.avisoEl.className = `aviso ${tipo}`;
    clearTimeout((this.avisoEl as unknown as { t?: number }).t);
    (this.avisoEl as unknown as { t?: number }).t = window.setTimeout(() => this.avisoEl.classList.add('oculto'), tipo === 'error' ? 9000 : 5000);
  }

  // ---------- Acciones ----------

  async refrescarResumen(): Promise<void> {
    try {
      const r = await this.db.resumen();
      this.almacen.set({ resumen: r, epsg: r.epsg ?? this.almacen.estado.epsg });
    } catch { /* la base aun no esta lista */ }
  }

  async refrescarCandidatos(): Promise<void> {
    try {
      this.almacen.set({ candidatos: await this.db.candidatos() });
    } catch { /* idem */ }
  }

  private ultimaPeticionAgua = 0;
  /** Rios y arroyos cargados en la vista (desde el zoom 10: son pocos y largos). */
  async refrescarAgua(): Promise<void> {
    const e = this.almacen.estado;
    if (!e.resumen || e.resumen.cursosAgua === 0 || this.mapa.map.getZoom() < 10) {
      this.mapa.setAgua({ type: 'FeatureCollection', features: [] });
      return;
    }
    const { bbox, epsg } = this.mapa.bboxVistaUtm(e.resumen.epsg ?? undefined);
    if (e.resumen.epsg && epsg !== e.resumen.epsg) return;
    const id = ++this.ultimaPeticionAgua;
    const datos = await this.db.aguaGeoJson(bbox);
    if (id !== this.ultimaPeticionAgua) return;
    this.mapa.setAgua(datos);
  }

  private ultimaPeticionZona = 0;
  async refrescarZona(): Promise<void> {
    const e = this.almacen.estado;
    void this.refrescarAgua().catch(() => undefined);
    if (!e.resumen || e.resumen.parcelas === 0 || this.mapa.map.getZoom() < ZOOM_VECTOR) {
      this.mapa.setZona({ type: 'FeatureCollection', features: [] });
      return;
    }
    const { bbox, epsg } = this.mapa.bboxVistaUtm(e.resumen.epsg ?? undefined);
    if (e.resumen.epsg && epsg !== e.resumen.epsg) return;
    const id = ++this.ultimaPeticionZona;
    const datos = await this.db.parcelasGeoJson(bbox);
    if (id !== this.ultimaPeticionZona) return;
    this.mapa.setZona(datos);
  }

  private ultimaPeticionPartes = 0;
  async refrescarPartes(): Promise<void> {
    const e = this.almacen.estado;
    if (!e.resumen || e.resumen.parcelas === 0 || this.mapa.map.getZoom() < 14) return;
    const { bbox, epsg } = this.mapa.bboxVistaUtm(e.resumen.epsg ?? undefined);
    const id = ++this.ultimaPeticionPartes;
    // Descarga bajo demanda de las partes que falten en la vista (solo teselas ya cargadas).
    await this.db.cargarPartes(bbox, epsg, 7).catch(() => undefined);
    const datos = await this.db.partesGeoJson(bbox);
    if (id !== this.ultimaPeticionPartes) return;
    for (const f of datos.features) {
      const plantas = Number(f.properties.plantasSobre ?? 0) || 1;
      const cumbrera = f.properties.cumbreraM;
      f.properties.altura = typeof cumbrera === 'number' && cumbrera > 0 ? cumbrera : plantas * 3;
    }
    this.mapa.setPartes(datos);
    await this.refrescarZona();
  }

  async seleccionar(rc: string | null, centrar = false): Promise<void> {
    this.almacen.set({ rcSeleccionado: rc });
    this.mapa.setSeleccion(rc);
    if (!rc) { this.ficha.cerrar(); return; }
    const f = await this.db.ficha(rc);
    if (f) {
      this.ficha.mostrar(f);
      if (centrar) this.mapa.encajarBbox([f.parcela.minx, f.parcela.miny, f.parcela.maxx, f.parcela.maxy], f.parcela.epsg, 18);
    } else {
      this.ficha.mostrar(null, { rc, direccion: null });
      if (centrar) await this.localizarReferencia(rc);
    }
  }

  private async clic(rc: string | null, lonLat: [number, number]): Promise<void> {
    if (rc) { await this.seleccionar(rc); return; }
    if (this.mapa.map.getZoom() < 15) { await this.seleccionar(null); return; }
    // Fuera de la zona cargada: se pregunta al Catastro que parcela hay en el punto.
    const epsg = husoParaLonLat(lonLat[0], lonLat[1]);
    const u = aUtm(lonLat[0], lonLat[1], epsg);
    try {
      const r = await consultarRcPorCoordenadas(Math.round(u.x * 100) / 100, Math.round(u.y * 100) / 100, epsg);
      if (!r) { await this.seleccionar(null); return; }
      this.almacen.set({ rcSeleccionado: r.rc });
      this.mapa.setSeleccion(null);
      const f = await this.db.ficha(r.rc);
      this.ficha.mostrar(f, { rc: r.rc, direccion: r.direccion, epsg });
    } catch (err) {
      this.almacen.avisar(`Catastro: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  async alternarCandidato(rc: string): Promise<void> {
    const existe = this.almacen.estado.candidatos.some((c) => c.rc === rc);
    if (existe) await this.db.borrarCandidato(rc);
    else await this.db.guardarCandidato({ rc, anuncioUrl: null, precio: null, etiqueta: null, notas: null, creado: Date.now() });
    await this.refrescarCandidatos();
    await this.refrescarZona();
    if (this.ficha.rcActual === rc) {
      const f = await this.db.ficha(rc);
      this.ficha.mostrar(f, f ? undefined : { rc, direccion: null });
    }
    if (this.almacen.estado.resultados.length) this.almacen.set({ resultados: [...this.almacen.estado.resultados] });
  }

  /** Buscador de la cabecera: referencia, coordenadas o texto (CartoCiudad). */
  private async buscarLugar(texto: string): Promise<void> {
    const t = texto.trim();
    if (!t) return;
    const rc = esReferenciaCatastral(t);
    if (rc) { await this.seleccionar(rc.rc14, true); return; }
    const coord = esCoordenada(t);
    if (coord) {
      if ('lat' in coord) this.mapa.irA(coord.lon, coord.lat, 17);
      else {
        const epsg = coord.huso === 28 ? 4083 : 25800 + coord.huso;
        const [lon, lat] = aLonLat(coord.x, coord.y, epsg);
        this.mapa.irA(lon, lat, 17);
      }
      this.cerrarMenu();
      return;
    }
    try {
      const lugar = await buscarLugar(t);
      if (!lugar) { this.almacen.avisar('CartoCiudad no encontró ese lugar.', 'error'); return; }
      const zoom = lugar.tipo === 'Municipio' || lugar.tipo === 'municipio' ? 13 : lugar.tipo === 'provincia' ? 9 : 17;
      this.mapa.irA(lugar.lon, lugar.lat, zoom);
      this.cerrarMenu();
    } catch (err) {
      this.almacen.avisar(`Error al geocodificar: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }

  /**
   * Parcela por referencia cuando no esta en la base local: se pide su geometria al WFS y, si
   * `encajar`, se encuadra. Devuelve su centro (lon, lat) o null.
   */
  private async localizarReferencia(rc: string, encajar = true): Promise<[number, number] | null> {
    try {
      const epsg = this.almacen.estado.epsg;
      const xml = await descargarGml(urlParcelaPorRc(rc, epsg));
      const { registros, epsg: epsgGml } = parsearParcelas(xml);
      const p = registros[0];
      if (!p || p.geom.length === 0) { this.almacen.avisar('El Catastro no devolvió esa referencia.', 'error'); return null; }
      const b = bboxMultiPoligono(p.geom);
      if (encajar) this.mapa.encajarBbox(b, epsgGml ?? epsg, 18);
      return aLonLat((b[0] + b[2]) / 2, (b[1] + b[3]) / 2, epsgGml ?? epsg);
    } catch (err) {
      this.almacen.avisar(`No se pudo localizar la referencia: ${err instanceof Error ? err.message : String(err)}`, 'error');
      return null;
    }
  }
}
